<script setup>
import { computed } from 'vue'
import FormInput from '@/components/Atoms/FormInput.vue'
import FormTextarea from '@/components/Atoms/FormTextarea.vue'
import FormSelect from '@/components/Atoms/FormSelect.vue'
import FormSwitch from '@/components/Atoms/FormSwitch.vue'
import SiteDisplayThumb from './SiteDisplayThumb.vue'
import { DISPLAY_CARDS, DISPLAY_DEFAULTS, DISPLAY_PRESETS, isWritable, sameValue, clone } from './siteDisplayOptions'

// "Display" section of Theme & settings: the curated runtime display options
// (siteDisplayOptions.js) as cards. `modelValue` is the working draft of every
// display key; `base` holds the values currently on the runtime so the parent
// can PATCH only what changed. Controls whose key the runtime does not list in
// `writableKeys` are not rendered.
const props = defineProps({
	modelValue: {
		type: Object,
		required: true,
	},
	base: {
		type: Object,
		required: true,
	},
	writableKeys: {
		type: Array,
		default: null,
	},
	categories: {
		type: Array,
		default: () => [],
	},
	siteId: {
		type: String,
		required: true,
	},
	errors: {
		type: Object,
		default: () => ({}),
	},
	/** Card ids to render (`layout | sections | texts | player`); all when null. */
	cards: {
		type: Array,
		default: null,
	},
	showPresets: {
		type: Boolean,
		default: true,
	},
	/** `true` → the cards become a single column (used beside the preview pane). */
	singleColumn: {
		type: Boolean,
		default: false,
	},
	/**
	 * Keys the site's home-page template does not read (`templateIgnoredKeys`
	 * on `GET /settings`, sites-broadcast-template-contract.md §2.4): still
	 * stored and honoured on the content pages, so the control stays visible
	 * but greyed and inert.
	 */
	ignoredKeys: {
		type: Array,
		default: () => [],
	},
	/** Name of the template for the "not used by …" hint on ignored controls. */
	templateLabel: {
		type: String,
		default: 'Broadcast',
	},
})
const emit = defineEmits(['update:modelValue'])

const draft = computed(() => props.modelValue)
const set = (key, value) => emit('update:modelValue', { ...props.modelValue, [key]: value })
const setMany = (values) => {
	const next = { ...props.modelValue }
	for (const [key, value] of Object.entries(values)) if (isWritable(key, props.writableKeys)) next[key] = clone(value)
	emit('update:modelValue', next)
}

const visibleControls = (card) => card.controls.filter((c) => isWritable(c.key, props.writableKeys) && (!c.showWhen || Object.entries(c.showWhen).every(([k, v]) => draft.value[k] === v)))
const cards = computed(() => DISPLAY_CARDS
	.filter((card) => !props.cards || props.cards.includes(card.id))
	.map((card) => ({ ...card, visible: visibleControls(card) }))
	.filter((card) => card.visible.length))

const isDefault = (key) => sameValue(draft.value[key], DISPLAY_DEFAULTS[key])
const isChanged = (key) => !sameValue(draft.value[key], props.base[key])
const cardKeys = (card) => card.controls.map((c) => c.key).filter((k) => isWritable(k, props.writableKeys))
const cardAllDefault = (card) => cardKeys(card).every(isDefault)
const cardChanged = (card) => cardKeys(card).some(isChanged)
const resetCard = (card) => setMany(Object.fromEntries(cardKeys(card).map((k) => [k, DISPLAY_DEFAULTS[k]])))

const presets = computed(() => DISPLAY_PRESETS.map((p) => {
	const keys = Object.keys(p.values).filter((k) => isWritable(k, props.writableKeys))
	return { ...p, active: keys.length > 0 && keys.every((k) => sameValue(draft.value[k], p.values[k])) }
}))

const categoryOptions = computed(() => [
	{ value: 0, text: DISPLAY_DEFAULTS.allVideosText === draft.value.allVideosText || !draft.value.allVideosText ? 'All videos' : `${draft.value.allVideosText} (all videos)` },
	...props.categories.map((c) => ({ value: Number(c.id), text: c.name })),
])

const GRID = { 2: 'sm:grid-cols-2', 3: 'sm:grid-cols-3', 4: 'sm:grid-cols-4', 5: 'sm:grid-cols-5' }
const choiceGrid = (n) => GRID[Math.min(n, 5)] || 'sm:grid-cols-3'
// '' and 0 are the runtime's "follow the experience" sentinels → one stable id for the Default tile.
const optionId = (key, value) => `display-${key}-${value === '' || value === 0 ? 'default' : String(value)}`
const isIgnored = (key) => Array.isArray(props.ignoredKeys) && props.ignoredKeys.includes(key)
</script>

<template>
	<div class="flex flex-col gap-4" data-testid="site-display">
		<section v-if="showPresets" class="rounded-lg bg-surface-2 p-4 sm:p-5" data-testid="display-presets">
			<div class="flex flex-wrap items-start justify-between gap-3">
				<div class="min-w-0">
					<h3 class="mb-1 text-sm font-semibold text-white">Presets</h3>
					<p class="mb-0 text-tiny text-surface-7">Start from a look, then fine-tune below. Nothing changes until you save.</p>
				</div>
			</div>
			<!--
				The form column is as narrow as ~430 px beside the live preview with the app
				sidebar expanded (Trello fnjLBE2H / oy0Nntak): the cards follow the container,
				not the viewport — one column until two ≥ 17 rem cards fit, the thumbnail
				above the text until a card is ≥ 20 rem wide, and the Applied badge wraps
				under the title instead of running out of the card.
			-->
			<div class="mt-4 grid grid-cols-[repeat(auto-fit,minmax(min(100%,17rem),1fr))] gap-3" data-testid="display-presets-grid">
				<button v-for="preset in presets" :key="preset.id" type="button"
					class="preset-card min-w-0 rounded-lg border p-3 text-left transition-colors"
					:class="preset.active ? 'border-main bg-main-transparent' : 'border-surface-4 bg-surface-3 hover:border-surface-6'"
					:aria-pressed="preset.active" :data-testid="`display-preset-${preset.id}`" @click="setMany(preset.values)">
					<span class="preset-body">
						<span class="block w-24 shrink-0"><SiteDisplayThumb :kind="preset.thumb" :active="preset.active" /></span>
						<span class="block min-w-0 flex-1">
							<span class="flex flex-wrap items-center gap-x-2 gap-y-1 text-sm font-medium text-white">
								<span>{{ preset.title }} preset</span>
								<span v-if="preset.active" class="shrink-0 whitespace-nowrap rounded-full bg-main px-1.5 py-px text-[10px] font-medium uppercase tracking-wide text-white" data-testid="display-preset-applied">Applied</span>
							</span>
							<span class="mt-0.5 block text-tiny text-surface-7">{{ preset.description }}</span>
						</span>
					</span>
				</button>
			</div>
		</section>

		<div class="grid grid-cols-1 gap-4" :class="singleColumn ? '' : 'lg:grid-cols-2'">
			<section v-for="card in cards" :key="card.id" class="rounded-lg bg-surface-2 p-4 sm:p-5" :data-testid="`display-card-${card.id}`">
				<div class="mb-4 flex flex-wrap items-start justify-between gap-2">
					<div class="min-w-0">
						<h3 class="mb-0.5 flex items-center gap-2 text-sm font-semibold text-white">
							{{ card.title }}
							<span v-if="cardChanged(card)" class="rounded-full bg-warning/15 px-1.5 py-px text-[10px] font-medium uppercase tracking-wide text-warning">Unsaved</span>
						</h3>
						<p class="mb-0 text-tiny text-surface-7">{{ card.description }}</p>
					</div>
					<button type="button" class="shrink-0 text-tiny text-surface-8 underline hover:text-white disabled:no-underline disabled:opacity-50"
						:disabled="cardAllDefault(card)" :aria-label="`Reset ${card.title.toLowerCase()} to defaults`" @click="resetCard(card)">
						Reset to defaults
					</button>
				</div>

				<div class="flex flex-col gap-4">
					<div v-for="control in card.visible" :key="control.key" class="min-w-0" :class="isIgnored(control.key) ? 'opacity-50' : ''" :data-ignored="isIgnored(control.key) || undefined" :data-testid="`display-control-${control.key}`">
						<p v-if="isIgnored(control.key)" class="mb-1 text-[11px] font-medium uppercase tracking-wide text-surface-7" :data-testid="`display-ignored-${control.key}`">Not used by the {{ templateLabel }} template</p>
						<div :class="isIgnored(control.key) ? 'pointer-events-none select-none' : ''" :aria-disabled="isIgnored(control.key) || undefined">
							<!-- Choice cards with a thumbnail -->
							<template v-if="control.type === 'choice'">
								<p class="mb-0.5 text-tiny font-medium text-surface-text-1">{{ control.label }}</p>
								<p v-if="control.help" class="mb-2 text-tiny text-surface-7">{{ control.help }}</p>
								<div class="grid grid-cols-2 gap-2" :class="choiceGrid(control.options.length)" role="radiogroup" :aria-label="control.label">
									<button v-for="option in control.options" :key="String(option.value)" type="button" role="radio"
										class="min-w-0 rounded-lg border p-1.5 text-left transition-colors"
										:class="draft[control.key] === option.value ? 'border-main bg-main-transparent' : 'border-surface-4 bg-surface-3 hover:border-surface-6'"
										:aria-checked="draft[control.key] === option.value" :data-testid="optionId(control.key, option.value)"
										@click="set(control.key, option.value)">
										<SiteDisplayThumb v-if="control.thumb" :kind="control.thumb" :value="option.value" :active="draft[control.key] === option.value" />
										<span class="mt-1.5 block truncate text-tiny font-medium" :class="draft[control.key] === option.value ? 'text-white' : 'text-surface-8'">{{ option.text }}</span>
										<span class="block truncate text-[11px] text-surface-7">
											{{ option.hint || '' }}<template v-if="option.value === DISPLAY_DEFAULTS[control.key] && option.text !== 'Default'">{{ option.hint ? ' · ' : '' }}default</template>
										</span>
									</button>
								</div>
							</template>

							<!-- Boolean -->
							<div v-else-if="control.type === 'switch'" class="rounded-lg bg-surface-3 px-3 py-2.5">
								<FormSwitch :id="`display-${control.key}`" :model-value="control.invert ? !draft[control.key] : Boolean(draft[control.key])"
									:label="control.label" label-position="left" label-styles="w-full justify-between gap-3 text-sm text-surface-text-1"
									@update:model-value="set(control.key, control.invert ? !$event : $event)" />
								<p v-if="control.help" class="mb-0 mt-0.5 pr-14 text-tiny text-surface-7">{{ control.help }}</p>
							</div>

							<!-- Text -->
							<template v-else-if="control.type === 'text'">
								<FormInput :id="`display-${control.key}`" :model-value="draft[control.key]" :label="control.label" :type="control.inputType || 'text'"
									:placeholder="control.placeholder || DISPLAY_DEFAULTS[control.key] || ''" :maxlength="control.maxlength" :is-error="Boolean(errors[control.key])"
									class="min-w-0" @update:model-value="set(control.key, $event)" />
								<p v-if="errors[control.key]" class="mb-0 mt-1.5 text-tiny text-error">{{ errors[control.key] }}</p>
								<p v-else-if="control.help" class="mb-0 mt-1.5 text-tiny text-surface-7">{{ control.help }}</p>
								<p v-else-if="DISPLAY_DEFAULTS[control.key]" class="mb-0 mt-1.5 text-tiny text-surface-7">Default “{{ DISPLAY_DEFAULTS[control.key] }}”. Clear the field to use it.</p>
							</template>

							<template v-else-if="control.type === 'textarea'">
								<FormTextarea :id="`display-${control.key}`" :model-value="draft[control.key]" :label="control.label" :maxlength="control.maxlength" :hint="control.help"
									placeholder="A sentence or two about what viewers will find here." @update:model-value="set(control.key, $event)" />
							</template>

							<!-- Runtime category id -->
							<template v-else-if="control.type === 'category'">
								<FormSelect :id="`display-${control.key}`" :model-value="draft[control.key]" :label="control.label" :options="categoryOptions" @update:model-value="set(control.key, Number($event))" />
								<p class="mb-0 mt-1 text-tiny text-surface-7">{{ categories.length ? control.help : 'Add categories under the Categories tab to pick one here.' }}</p>
							</template>
						</div>
					</div>
				</div>
			</section>
		</div>
	</div>
</template>

<style scoped>
/* Preset cards lay themselves out by their own width (sidebar expanded or not). */
.preset-card {
	container-type: inline-size;
}
.preset-body {
	display: flex;
	flex-direction: column;
	gap: 0.625rem;
}
@container (min-width: 20rem) {
	.preset-body {
		flex-direction: row;
		align-items: flex-start;
		gap: 0.75rem;
	}
}
</style>
